/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchPlans,
  updatePlan,
  deletePlan as _deletePlan,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Plan, LoadStatus } from "../../../types";

export interface PlansState {
  plans: Record<string, Plan>;
  planEdits: Record<string, Edits<Plan>>;
  planLoadStatus: LoadStatus;
}

const initialState: PlansState = {
  plans: {},
  planEdits: {},
  planLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadPlans = createAsyncThunk(
  "plans/loadPlans",
  async (_, thunkAPI): Promise<Plan[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchPlans();
  }
);

export const addOrUpdatePlan = createAsyncThunk(
  "plans/addOrUpdatePlan",
  async (data: Partial<Plan>, thunkAPI): Promise<Plan> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updatePlan(state.login.accessToken, data);
  }
);

export const deletePlan = createAsyncThunk(
  "plans/deletePlan",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deletePlan(state.login.accessToken, id);
  }
);

export const revertPlan = createAsyncThunk(
  "plans/revertPlan",
  async (id: string, thunkAPI): Promise<Plan> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.plans.planEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updatePlan(state.login.accessToken, edits.oldData);
  }
);

export const plansSlice = createSlice({
  name: "plans",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load plans
      .addCase(loadPlans.pending, (state) => {
        state.planLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadPlans.fulfilled, (state, action) => {
        for (const plan of action.payload) {
          state.plans[plan.id] = plan;
          state.planEdits[plan.id] = {
            oldData: plan,
          };
        }
        state.planLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadPlans.rejected, (state) => {
        state.planLoadStatus = LoadStatus.FAILED;
      })
      // add or update plan
      .addCase(addOrUpdatePlan.fulfilled, (state, action) => {
        const plan = action.payload;
        if (state.plans[plan.id]) {
          state.planEdits[plan.id].newData = plan;
        } else {
          state.planEdits[plan.id] = {
            oldData: plan,
            created: true,
          };
        }
        state.plans[plan.id] = plan;
      })
      // delete plan
      .addCase(deletePlan.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.plans[id];
      })
      // revert plan
      .addCase(revertPlan.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.plans[id] = action.payload;
        state.planEdits[id].oldData = action.payload;
        state.planEdits[id].newData = undefined;
      });
  },
});

export default plansSlice.reducer;
